'use client';

import { CreateMemoToolUI, UpdateMemoToolUI } from './memo-tool-ui';
import { InstantPreviewToolUI } from './preview-tool-ui';
import { ExportMomentToolUI, ExportMemoToolUI } from './export-tool-ui';
import { CreateCollectionUI, AddedToCollectionUI, CollectionPickerUI } from './collection-tool-ui';
import { CompoundVideoUI } from './compound-video-tool-ui';
import { PreviewMomentEditsUI } from './preview-moment-edits-tool-ui';
import { ExportTranscriptToolUI } from './export-transcript-tool-ui';
import { WorkflowChoiceToolUI } from './workflow-choice-tool-ui';
import { SearchMomentsToolUI } from './search-moments-tool-ui';
import { SpeakerPostToolUI } from '@/components/momento/social-posts/speaker-post-tool-ui';
import { GenericPostToolUI } from '@/components/momento/social-posts/generic-post-tool-ui';
import { makeAssistantToolUI } from '@assistant-ui/react';
import { ComponentType } from 'react';

/**
 * Props adapter for Tool UIs
 *
 * assistant-ui spreads tool-call part properties directly as props:
 *   { toolCallId, toolName, args, result, output, addResult, resume, ... }
 *
 * Our Tool UIs expect the legacy format:
 *   { part: { toolCallId, toolName, args, result }, status: 'loading' | 'done' }
 *
 * This wrapper adapts the props to maintain backward compatibility.
 * NOTE: AI SDK data stream uses 'output' for tool results, but our components expect 'result'.
 */
function wrapToolUI(Component: ComponentType<{ part: any; status: string }>) {
  return function ToolUIWrapper(props: any) {
    const { toolCallId, toolName, args, result, output, addResult, resume, ...rest } = props;

    // AI SDK data stream uses 'output', but our components expect 'result'
    // Prefer 'result' if present (transformed stream), fallback to 'output' (raw stream)
    const toolResult = result ?? output;

    // Reconstruct 'part' object from spread props
    const part = {
      toolCallId,
      toolName,
      args,
      result: toolResult,
      ...rest, // Include any additional properties
    };

    // Derive status from result presence
    // 'loading' if result is undefined/null, 'done' if result exists
    const status = toolResult === undefined || toolResult === null ? 'loading' : 'done';

    return <Component part={part} status={status} />;
  };
}

/**
 * Tool UI Registry
 * Maps tool names to their UI components
 *
 * All Tool UIs are wrapped with wrapToolUI() to adapt assistant-ui's
 * spread props format to our { part, status } format.
 *
 * CRITICAL: Tool names MUST match the key used in agent's tools object (camelCase),
 * NOT the tool's id (kebab-case). Mastra streams use the object key.
 *
 * Example:
 *   Agent: tools: { createMemo: createMemoTool, ... }
 *   Registry: 'createMemo': makeAssistantToolUI({ toolName: 'createMemo', ... })
 */
export const toolUIRegistry = {
  // === Phase 2: Core Tools ===

  // Memo tools
  'createMemo': makeAssistantToolUI({
    toolName: 'createMemo',
    render: wrapToolUI(CreateMemoToolUI),
  }),
  'updateMemo': makeAssistantToolUI({
    toolName: 'updateMemo',
    render: wrapToolUI(UpdateMemoToolUI),
  }),

  // Preview tools
  'instantPreview': makeAssistantToolUI({
    toolName: 'instantPreview',
    render: wrapToolUI(InstantPreviewToolUI),
  }),

  // Export tools
  'exportMoment': makeAssistantToolUI({
    toolName: 'exportMoment',
    render: wrapToolUI(ExportMomentToolUI),
  }),
  'exportMemo': makeAssistantToolUI({
    toolName: 'exportMemo',
    render: wrapToolUI(ExportMemoToolUI),
  }),

  // === Phase 3: Collections & Compound Video ===

  // Collection tools
  'createCollection': makeAssistantToolUI({
    toolName: 'createCollection',
    render: wrapToolUI(CreateCollectionUI),
  }),
  'addMomentToCollection': makeAssistantToolUI({
    toolName: 'addMomentToCollection',
    render: wrapToolUI(AddedToCollectionUI),
  }),
  'listCollections': makeAssistantToolUI({
    toolName: 'listCollections',
    render: wrapToolUI(CollectionPickerUI),
  }),

  // Video compilation
  'compoundVideo': makeAssistantToolUI({
    toolName: 'compoundVideo',
    render: wrapToolUI(CompoundVideoUI),
  }),

  // Preview edits
  'previewMomentEdits': makeAssistantToolUI({
    toolName: 'previewMomentEdits',
    render: wrapToolUI(PreviewMomentEditsUI),
  }),

  // Transcript export
  'exportTranscript': makeAssistantToolUI({
    toolName: 'exportTranscript',
    render: wrapToolUI(ExportTranscriptToolUI),
  }),

  // === Phase 4: Workflows ===

  // Workflow tools (interactive suspend/resume)
  'createContentWorkflow': makeAssistantToolUI({
    toolName: 'createContentWorkflow',
    render: wrapToolUI(WorkflowChoiceToolUI),
  }),
  'discoverContentWorkflow': makeAssistantToolUI({
    toolName: 'discoverContentWorkflow',
    render: wrapToolUI(WorkflowChoiceToolUI),
  }),

  // === Phase 5: Social Post Previews ===

  // Social post preview tools
  'generateSpeakerPost': makeAssistantToolUI({
    toolName: 'generateSpeakerPost',
    render: wrapToolUI(SpeakerPostToolUI),
  }),
  'createPostFromMoment': makeAssistantToolUI({
    toolName: 'createPostFromMoment',
    render: wrapToolUI(GenericPostToolUI),
  }),

  // === Phase 6: Search Results ===

  // Search tool with visual card rendering
  'searchMoments': makeAssistantToolUI({
    toolName: 'searchMoments',
    render: wrapToolUI(SearchMomentsToolUI),
  }),
};

/**
 * Get all tool UIs as an array for runtime registration
 */
export const toolUIs = Object.values(toolUIRegistry);

/**
 * Coverage Stats:
 * - Total Mastra tools: 32 (30 direct tools + 2 workflow tools)
 * - Tools with UI: 15 (47%)
 * - Context tools (no UI needed): 8 (25%)
 * - Remaining: 9 (28%)
 */
